// src/screens/MainMenu.jsx
import React, { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import "../App.css";
import { supabase } from "../supabaseClient";
import { getSessionUser } from "../lib/sessionUser";
import {
  loadProgress,
  loadProgressRemote,
  resetProgress,
} from "../data/progressStorage";
import {
  loadVerbProgress,
  resetVerbProgress,
} from "../data/verbProgressStorage"; // Verben
import {
  loadSentenceClozeProgress,
  resetSentenceClozeProgress,
} from "../data/sentenceClozeProgressStorage"; // Lückentexte
import {
  loadPhraseProgress,
  resetPhraseProgress,
} from "../data/phraseProgressStorage"; // Redewendungen
import {
  getPhrasesPerWeek,
  getPhraseWeekPlan,
} from "../data/phraseSchedule";
import { labelForLanguage, parseLanguageId } from "../data/languages";
import {
  computeWeekExp,
  canUnlockNextWeek,
  computeVerbsPercent,
  getGapsTargetForPace,
} from "../data/weekExp";
import { getTranslationWeekTarget } from "../data/translationPlan";
import { loadTotalExp } from "../data/totalExp";
import {
  loadTranslationWeekCount,
  resetTranslationProgress,
} from "../data/translationProgressStorage"; // Freie Übersetzung
import {
  loadUnlockedWeek,
  loadUnlockedWeekLocal,
  saveUnlockedWeek,
  resetUnlockedWeek,
  loadUnlockedAt,
  saveUnlockedAt,
  loadWeekExpCache,
  saveWeekExpCache,
} from "../data/weekUnlockStorage";
import {
  Badge,
  Button,
  Eyebrow,
  Icon,
  Note,
  Panel,
  ProgressBar,
  Stack,
} from "../components/ui";
import { TARGET_WORDS, PACE_YEARS, PACE_LABELS } from "../data/learningConfig";
import { loadFriendDuels, publishFriendStats } from "../lib/friends";
import { AttackBanner } from "./Friends";

const WEEKLY_KEY_PREFIX = "fluent_weeklyset_";

function getWeeklyKey(
  languageId,
  pace,
  totalWords,
  profileId,
  weekIndex = 1
) {
  const lang = languageId || "generic";
  const p = pace || "medium";
  const t = totalWords || 0;
  const profile = profileId || "default";
  const w = weekIndex || 1;
  return `${WEEKLY_KEY_PREFIX}${profile}_${lang}_${p}_${t}_w${w}`;
}

function loadWeeklySetLocal(
  languageId,
  pace,
  totalWords,
  profileId,
  weekIndex = 1
) {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(
      getWeeklyKey(languageId, pace, totalWords, profileId, weekIndex)
    );
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (Array.isArray(data) && data.length > 0) return data;
    return null;
  } catch (e) {
    console.warn("Fluent! weekly set load error (MainMenu, local):", e);
    return null;
  }
}

function resetWeeklySet(languageId, pace, totalWords, profileId, maxWeeks = 3) {
  if (typeof window === "undefined") return;
  try {
    for (let w = 1; w <= maxWeeks; w++) {
      window.localStorage.removeItem(
        getWeeklyKey(languageId, pace, totalWords, profileId, w)
      );
    }
  } catch (e) {
    console.warn("Fluent! weekly set reset error (MainMenu):", e);
  }
}

// EXP-Stand beim Öffnen einer Aufgabe. Bleibt erhalten, während das Hauptmenü
// ausgeblendet ist, damit beim Zurückkommen die neu gesammelten EXP animiert werden.
let expSnapshot = null;

// Ablauf der EXP-Animation: [nächster Schritt, Wartezeit in ms]
const EXP_GAIN_STEPS = {
  start: ["tasks", 80], // heller Abschnitt sichtbar, Balken noch auf altem Stand
  tasks: ["fly", 900], // Aufgaben-Balken laden auf, „+EXP“ steht daneben
  fly: ["total", 650], // „+EXP“ fliegt in den großen Balken
  total: [null, 1200], // großer Balken lädt auf, Summe springt um
};

function prefersReducedMotion() {
  return (
    typeof window !== "undefined" &&
    window.matchMedia &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}


/** Default-Phrasen-Pensum je nach Tempo (gilt auch für Themen-Wochen) */
const getPhrasesTargetForPace = getPhrasesPerWeek;

export default function MainMenu({
  language,
  pace,
  onBack,
  onOpenFlashcards,
  onOpenVerbs,
  onOpenGaps, // Lückentexte
  onOpenPhrases, // Redewendungen
  onOpenTranslations, // Freie Übersetzung
  onOpenFriends,
  onOpenDefense, // Blitzabfrage gegen einen Angriff
  profileId,
  onOpenDebugVocab,
  onOpenDiagnostics,
  userEmail,
  onLogout,
  onHardReset,
  initialWeek, // Woche, in der zuletzt geübt wurde (sonst die neueste)
}) {
  const [showSettings, setShowSettings] = useState(false);

  const years = PACE_YEARS[pace] ?? PACE_YEARS.medium;
  const weeks = Math.round(years * 52);
  const weeklyWords = Math.ceil(TARGET_WORDS / weeks);

  const langId = language || "es-de";
  const paceValue = pace || "medium";
  const profile = profileId || "default";

  const [weeklySet, setWeeklySet] = useState([]);
  const [progress, setProgress] = useState({});
  const [progressLoaded, setProgressLoaded] = useState(false);

  // Verb-Progress (pro Woche)
  const [verbStates, setVerbStates] = useState({});

  // Lückentext-Progress der angezeigten Woche (jede Woche zählt für sich, kein Vorarbeiten)
  const [gapsCountWeek, setGapsCountWeek] = useState(0);
  // Für welche Woche der Zähler geladen ist (Schutz gegen Werte der Vorwoche)
  const [gapsWeek, setGapsWeek] = useState(null);
  const [gapsWeeklyTarget, setGapsWeeklyTarget] = useState(
    getGapsTargetForPace(paceValue)
  );

  // Übersetzungs-Progress der angezeigten Woche (null-Woche = noch nicht geladen)
  const [translationCountWeek, setTranslationCountWeek] = useState(0);
  const [translationsWeek, setTranslationsWeek] = useState(null);

  // Phrasen-Progress der angezeigten Woche
  const [phraseCountWeek, setPhraseCountWeek] = useState(0);
  const [phrasesWeek, setPhrasesWeek] = useState(null);
  const [phrasesWeeklyTarget, setPhrasesWeeklyTarget] = useState(
    getPhrasesTargetForPace(paceValue)
  );

  // Freigeschaltete Woche nie über das Kursende (letzte Woche des Tempos) hinaus
  const clampToCourse = (week) => Math.max(1, Math.min(weeks, week));

  // Startwoche: die zuletzt geübte, falls sie schon freigeschaltet ist, sonst die neueste
  const initialWeekRef = useRef(initialWeek);
  const startWeekFor = (unlocked) => {
    const w = initialWeekRef.current;
    return w && w <= unlocked ? w : unlocked;
  };

  // Höchste freigeschaltete Woche: startet mit dem lokalen Stand,
  // Supabase kann sie danach nur noch erhöhen
  const [unlockedWeek, setUnlockedWeek] = useState(() =>
    clampToCourse(loadUnlockedWeekLocal(langId, paceValue, profile))
  );

  // Aktive (sichtbare) Woche im Hauptmenü
  const [currentWeek, setCurrentWeek] = useState(() =>
    startWeekFor(unlockedWeek)
  );
  // Erst nach dem Abgleich mit Supabase darf eine neue Woche freigeschaltet werden
  const [unlockSynced, setUnlockSynced] = useState(false);
  // Wann die höchste Woche freigeschaltet wurde (für die Montags-Regel bei 90 %)
  const [unlockedAt, setUnlockedAt] = useState(null);

  // Für welche Woche weeklySet / verbStates gerade geladen sind –
  // verhindert, dass Daten der vorherigen Woche eine Freischaltung auslösen
  const [weeklySetWeek, setWeeklySetWeek] = useState(null);
  const [verbStatesWeek, setVerbStatesWeek] = useState(null);

  // Zuletzt bekannte EXP pro Woche (für Karten außerhalb des Blicks)
  const [expCache, setExpCache] = useState(() =>
    loadWeekExpCache(langId, paceValue, profile)
  );

  const carouselRef = useRef(null);
  const initialScrollDone = useRef(false);

  // Freischaltung laden und zur aktuellen Woche springen
  useEffect(() => {
    let cancelled = false;
    initialScrollDone.current = false;
    const local = clampToCourse(
      loadUnlockedWeekLocal(langId, paceValue, profile)
    );
    setUnlockedWeek(local);
    setCurrentWeek(startWeekFor(local));
    setUnlockSynced(false);
    setUnlockedAt(null);
    setExpCache(loadWeekExpCache(langId, paceValue, profile));

    loadUnlockedWeek(langId, paceValue, profile).then(async (loaded) => {
      if (cancelled) return;
      const week = clampToCourse(loaded);
      if (week > local) {
        initialScrollDone.current = false;
        setUnlockedWeek(week);
        setCurrentWeek(startWeekFor(week));
      }
      const at = await loadUnlockedAt(langId, paceValue, profile, week);
      if (cancelled) return;
      setUnlockedAt(at);
      setUnlockSynced(true);
    });

    return () => {
      cancelled = true;
    };
    // clampToCourse/startWeekFor hängen nur von weeks bzw. einem Ref ab
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [langId, paceValue, profile, weeks]);

  // WeeklySet: eingeloggt → nur Supabase, sonst localStorage
  useEffect(() => {
    let cancelled = false;

    async function load() {
      let userId = null;
      let weekly = null;

      try {
        const { data: userData } = await getSessionUser();
        userId = userData?.user?.id || null;
      } catch (e) {
        console.warn("MainMenu: getUser failed:", e);
      }

      if (userId) {
        try {
          const { data, error } = await supabase
            .from("weekly_sets")
            .select("words")
            .eq("user_id", userId)
            .eq("profile_id", profile)
            .eq("language_id", langId)
            .eq("pace", paceValue)
            .eq("total_words", weeklyWords)
            .eq("week_index", currentWeek)
            .maybeSingle();

          if (
            !error &&
            data &&
            Array.isArray(data.words) &&
            data.words.length > 0
          ) {
            weekly = data.words;
          } else {
            weekly = [];
          }
        } catch (e) {
          console.warn("MainMenu: remote weekly_sets load failed:", e);
          weekly = [];
        }
      } else {
        weekly =
          loadWeeklySetLocal(
            langId,
            paceValue,
            weeklyWords,
            profile,
            currentWeek
          ) || [];
      }

      if (cancelled) return;
      setWeeklySet(weekly);
      setWeeklySetWeek(currentWeek);
    }

    load();

    return () => {
      cancelled = true;
    };
  }, [langId, paceValue, weeklyWords, profile, currentWeek]);

  /**
   * Progress (Vokabeln)
   */
  useEffect(() => {
    let cancelled = false;

    async function loadProgressState() {
      let userId = null;
      try {
        const { data: userData, error: userError } =
          await getSessionUser();
        if (!userError && userData?.user?.id) {
          userId = userData.user.id;
        }
      } catch (e) {
        console.warn("MainMenu: loadProgressState getUser failed:", e);
      }

      if (!userId) {
        const local = loadProgress(langId, profile) || {};
        if (!cancelled) setProgress(local);
        return;
      }

      try {
        const remote = await loadProgressRemote(userId, langId, profile);

        if (!remote) {
          if (!cancelled) setProgress({});
          return;
        }

        if (!cancelled) {
          setProgress(remote);
        }

        if (typeof window !== "undefined") {
          try {
            const key = `fluent_progress_${profile || "default"}_${
              langId || "generic"
            }`;
            window.localStorage.setItem(key, JSON.stringify(remote));
          } catch (e) {
            console.warn("MainMenu: mirror remote progress to local failed:", e);
          }
        }
      } catch (e) {
        console.warn("MainMenu: remote progress load exception:", e);
        if (!cancelled) setProgress({});
      }
    }

    loadProgressState().finally(() => {
      if (!cancelled) setProgressLoaded(true);
    });

    return () => {
      cancelled = true;
    };
  }, [langId, profile]);

  // Realtime für user_progress
  useEffect(() => {
    let cancelled = false;
    let channel = null;

    (async () => {
      try {
        const { data: userData, error: userError } =
          await getSessionUser();
        if (userError || !userData?.user?.id) return;
        const userId = userData.user.id;

        channel = supabase
          .channel(`progress-${userId}-${profile}-${langId}`)
          .on(
            "postgres_changes",
            {
              event: "*",
              schema: "public",
              table: "user_progress",
              filter: `user_id=eq.${userId}`,
            },
            async () => {
              if (cancelled) return;
              try {
                const remote = await loadProgressRemote(
                  userId,
                  langId,
                  profile
                );
                if (!remote) return;

                setProgress(remote);

                if (typeof window !== "undefined") {
                  try {
                    const key = `fluent_progress_${profile || "default"}_${
                      langId || "generic"
                    }`;
                    window.localStorage.setItem(
                      key,
                      JSON.stringify(remote)
                    );
                  } catch (e) {
                    console.warn(
                      "MainMenu: realtime mirror to local failed:",
                      e
                    );
                  }
                }
              } catch (e) {
                console.warn(
                  "MainMenu: realtime progress reload exception:",
                  e
                );
              }
            }
          )
          .subscribe();
      } catch (e) {
        console.warn("MainMenu: realtime setup failed:", e);
      }
    })();

    return () => {
      cancelled = true;
      if (channel) {
        supabase.removeChannel(channel);
      }
    };
  }, [langId, profile]);

  // Verb-Progress initial laden (für aktuelle Woche)
  useEffect(() => {
    let cancelled = false;

    (async () => {
      const states =
        (await loadVerbProgress(langId, profile, currentWeek)) || {};
      if (!cancelled) {
        setVerbStates(states);
        setVerbStatesWeek(currentWeek);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [langId, profile, currentWeek]);

  // Realtime für verb_progress (aktuelle Woche)
  useEffect(() => {
    let cancelled = false;
    let channel = null;

    (async () => {
      try {
        const { data: userData, error: userError } =
          await getSessionUser();
        if (userError || !userData?.user?.id) return;
        const userId = userData.user.id;

        channel = supabase
          .channel(
            `verb-progress-${userId}-${profile}-${langId}-w${currentWeek}`
          )
          .on(
            "postgres_changes",
            {
              event: "*",
              schema: "public",
              table: "verb_progress",
              filter: `user_id=eq.${userId},profile_id=eq.${profile},language_id=eq.${langId},week_index=eq.${currentWeek}`,
            },
            async () => {
              if (cancelled) return;
              const states =
                (await loadVerbProgress(langId, profile, currentWeek)) || {};
              if (!cancelled) {
                setVerbStates(states);
              }
            }
          )
          .subscribe();
      } catch (e) {
        console.warn("MainMenu: realtime verb_progress setup failed:", e);
      }
    })();

    return () => {
      cancelled = true;
      if (channel) {
        supabase.removeChannel(channel);
      }
    };
  }, [langId, profile, currentWeek]);

  // SentenceCloze-Progress initial laden
  useEffect(() => {
    let cancelled = false;

    async function loadClozeProgress() {
      try {
        const { data: userData, error: userError } =
          await getSessionUser();
        if (userError || !userData?.user?.id) {
          if (!cancelled) {
            setGapsCountWeek(0);
            setGapsWeek(currentWeek);
            setGapsWeeklyTarget(getGapsTargetForPace(paceValue));
          }
          return;
        }

        const userId = userData.user.id;
        const value = await loadSentenceClozeProgress(
          userId,
          profile,
          langId,
          paceValue,
          currentWeek
        );

        if (!cancelled) {
          const safeVal =
            typeof value === "number" && !Number.isNaN(value) ? value : 0;
          setGapsCountWeek(safeVal);
          setGapsWeek(currentWeek);
          setGapsWeeklyTarget(getGapsTargetForPace(paceValue));
        }
      } catch (e) {
        console.warn("MainMenu: loadClozeProgress failed:", e);
        if (!cancelled) {
          setGapsCountWeek(0);
          setGapsWeek(currentWeek);
          setGapsWeeklyTarget(getGapsTargetForPace(paceValue));
        }
      }
    }

    loadClozeProgress();
    return () => {
      cancelled = true;
    };
  }, [langId, profile, paceValue, currentWeek]);

  // Realtime für sentence_cloze_progress
  useEffect(() => {
    let cancelled = false;
    let channel = null;

    (async () => {
      try {
        const { data: userData, error: userError } =
          await getSessionUser();
        if (userError || !userData?.user?.id) return;
        const userId = userData.user.id;

        channel = supabase
          .channel(
            `sentence-cloze-${userId}-${profile}-${langId}-${paceValue}`
          )
          .on(
            "postgres_changes",
            {
              event: "*",
              schema: "public",
              table: "sentence_cloze_progress",
              filter: `user_id=eq.${userId},profile_id=eq.${profile},language_id=eq.${langId},pace=eq.${paceValue}`,
            },
            async () => {
              if (cancelled) return;
              try {
                const value = await loadSentenceClozeProgress(
                  userId,
                  profile,
                  langId,
                  paceValue,
                  currentWeek
                );
                const safeVal =
                  typeof value === "number" && !Number.isNaN(value)
                    ? value
                    : 0;
                setGapsCountWeek(safeVal);
              } catch (e) {
                console.warn(
                  "MainMenu: realtime loadClozeProgress failed:",
                  e
                );
              }
            }
          )
          .subscribe();
      } catch (e) {
        console.warn(
          "MainMenu: realtime sentence_cloze_progress setup failed:",
          e
        );
      }
    })();

    return () => {
      cancelled = true;
      if (channel) supabase.removeChannel(channel);
    };
  }, [langId, profile, paceValue, currentWeek]);

  // Übersetzungs-Progress der angezeigten Woche laden (Supabase + Browser-Speicher)
  useEffect(() => {
    let cancelled = false;
    loadTranslationWeekCount(profile, langId, paceValue, currentWeek)
      .catch((e) => {
        console.warn("MainMenu: loadTranslationWeekCount failed:", e);
        return 0;
      })
      .then((count) => {
        if (cancelled) return;
        setTranslationCountWeek(count);
        setTranslationsWeek(currentWeek);
      });
    return () => {
      cancelled = true;
    };
  }, [langId, profile, paceValue, currentWeek]);

  // Phrase-Progress initial laden
  useEffect(() => {
    let cancelled = false;

    async function loadPhrasesProgress() {
      try {
        const { data: userData, error: userError } =
          await getSessionUser();
        if (userError || !userData?.user?.id) {
          if (!cancelled) {
            setPhraseCountWeek(0);
            setPhrasesWeek(currentWeek);
            setPhrasesWeeklyTarget(getPhrasesTargetForPace(paceValue));
          }
          return;
        }

        const userId = userData.user.id;
        const value = await loadPhraseProgress(
          userId,
          profile,
          langId,
          paceValue,
          currentWeek
        );

        if (!cancelled) {
          const safeVal =
            typeof value === "number" && !Number.isNaN(value) ? value : 0;
          setPhraseCountWeek(safeVal);
          setPhrasesWeek(currentWeek);
          setPhrasesWeeklyTarget(getPhrasesTargetForPace(paceValue));
        }
      } catch (e) {
        console.warn("MainMenu: loadPhrasesProgress failed:", e);
        if (!cancelled) {
          setPhraseCountWeek(0);
          setPhrasesWeek(currentWeek);
          setPhrasesWeeklyTarget(getPhrasesTargetForPace(paceValue));
        }
      }
    }

    loadPhrasesProgress();
    return () => {
      cancelled = true;
    };
  }, [langId, profile, paceValue, currentWeek]);

  // Realtime für phrase_progress
  useEffect(() => {
    let cancelled = false;
    let channel = null;

    (async () => {
      try {
        const { data: userData, error: userError } =
          await getSessionUser();
        if (userError || !userData?.user?.id) return;
        const userId = userData.user.id;

        channel = supabase
          .channel(
            `phrase-progress-${userId}-${profile}-${langId}-${paceValue}`
          )
          .on(
            "postgres_changes",
            {
              event: "*",
              schema: "public",
              table: "phrase_progress",
              filter: `user_id=eq.${userId},profile_id=eq.${profile},language_id=eq.${langId},pace=eq.${paceValue}`,
            },
            async () => {
              if (cancelled) return;
              try {
                const value = await loadPhraseProgress(
                  userId,
                  profile,
                  langId,
                  paceValue,
                  currentWeek
                );
                const safeVal =
                  typeof value === "number" && !Number.isNaN(value)
                    ? value
                    : 0;
                setPhraseCountWeek(safeVal);
              } catch (e) {
                console.warn(
                  "MainMenu: realtime loadPhrasesProgress failed:",
                  e
                );
              }
            }
          )
          .subscribe();
      } catch (e) {
        console.warn(
          "MainMenu: realtime phrase_progress setup failed:",
          e
        );
      }
    })();

    return () => {
      cancelled = true;
      if (channel) supabase.removeChannel(channel);
    };
  }, [langId, profile, paceValue, currentWeek]);

  // "gemeistert" = Box 4 oder 5 innerhalb des Weekly-Sets der aktuellen Woche (Vokabeln)
  const masteredCount = weeklySet.filter((w) => {
    const state = progress[w.id];
    return state && state.box >= 4;
  }).length;

  const vocabDone = Math.min(masteredCount, weeklyWords);

  // ---------- VERB-PROGRESS-BERECHNUNG ----------
  const verbsPercent = computeVerbsPercent(verbStates);

  // ---------- LÜCKENTEXT-PROGRESS FÜR AKTUELLE WOCHE ----------
  const gapsDoneCurrentWeek = Math.min(gapsCountWeek, gapsWeeklyTarget);

  // ---------- PHRASES-PROGRESS FÜR AKTUELLE WOCHE ----------
  const phrasesDoneCurrentWeek = Math.min(phraseCountWeek, phrasesWeeklyTarget);

  // ---------- ÜBERSETZUNGS-PROGRESS FÜR AKTUELLE WOCHE ----------
  // Pensum wächst mit den Wochen (translationPlan.js)
  const translationsWeeklyTarget = getTranslationWeekTarget(currentWeek, paceValue);
  const translationsDoneCurrentWeek = Math.min(
    translationCountWeek,
    translationsWeeklyTarget
  );

  const tasks = getWeeklyTasks(
    pace,
    weeklyWords,
    vocabDone,
    verbsPercent,
    gapsDoneCurrentWeek,
    gapsWeeklyTarget,
    phrasesDoneCurrentWeek,
    phrasesWeeklyTarget,
    getPhraseWeekPlan(currentWeek, paceValue, parseLanguageId(langId)?.source),
    translationsDoneCurrentWeek,
    translationsWeeklyTarget
  );

  // ---------- EXP & FREISCHALTUNG ----------
  const exp = computeWeekExp({
    pace: paceValue,
    vocabBoxes: weeklySet.map((w) => (progress[w.id] && progress[w.id].box) || 0),
    vocabTarget: weeklyWords,
    verbsPercent,
    gapsDone: gapsDoneCurrentWeek,
    gapsTarget: gapsWeeklyTarget,
    phrasesDone: phrasesDoneCurrentWeek,
    phrasesTarget: phrasesWeeklyTarget,
    translationsDone: translationsDoneCurrentWeek,
    translationsTarget: translationsWeeklyTarget,
  });

  const weekDataReady =
    unlockSynced &&
    weeklySetWeek === currentWeek &&
    verbStatesWeek === currentWeek &&
    gapsWeek === currentWeek &&
    phrasesWeek === currentWeek &&
    translationsWeek === currentWeek;

  // ---------- EXP-ANIMATION BEIM ZURÜCKKOMMEN ----------
  // { parts: { [taskId]: { from, to } }, total: { from, to }, stage, flyers }
  const [expGain, setExpGain] = useState(null);
  const gainRefs = useRef({});
  const barRef = useRef(null);
  const snapshotKey = `${profile}|${langId}|${paceValue}|${currentWeek}`;

  const rememberExp = () => {
    if (!weekDataReady) return;
    const parts = {};
    Object.entries(exp.parts).forEach(([id, p]) => {
      parts[id] = p.exp;
    });
    expSnapshot = { key: snapshotKey, parts };
  };

  // Nach dem Laden mit dem gemerkten Stand vergleichen
  useEffect(() => {
    if (!weekDataReady || !progressLoaded || !expSnapshot) return;
    const snap = expSnapshot;
    expSnapshot = null;
    if (snap.key !== snapshotKey || prefersReducedMotion()) return;

    const parts = {};
    let gained = 0;
    Object.entries(exp.parts).forEach(([id, p]) => {
      const from = snap.parts[id];
      if (from != null && p.exp > from) {
        parts[id] = { from, to: p.exp };
        gained += p.exp - from;
      }
    });
    if (gained <= 0) return;

    setExpGain({
      parts,
      total: { from: exp.total - gained, to: exp.total },
      max: exp.max,
      stage: "start",
      flyers: [],
    });
  }, [weekDataReady, progressLoaded, snapshotKey, exp]);

  useEffect(() => {
    if (!expGain) return;
    const [nextStage, delay] = EXP_GAIN_STEPS[expGain.stage];
    const timer = setTimeout(() => {
      if (!nextStage) {
        setExpGain(null);
        return;
      }
      let flyers = expGain.flyers;
      if (nextStage === "fly") {
        // Start- und Zielpunkt der fliegenden Zahlen jetzt messen
        // Ziel: Mitte des neu gewonnenen Abschnitts im großen Balken
        const bar = barRef.current && barRef.current.getBoundingClientRect();
        const middle =
          expGain.max > 0
            ? (expGain.total.from + expGain.total.to) / 2 / expGain.max
            : 0;
        const target = bar && {
          x: bar.left + bar.width * Math.min(1, middle),
          y: bar.top + bar.height / 2,
        };
        flyers = Object.entries(expGain.parts)
          .map(([id, p]) => {
            const el = gainRefs.current[id];
            if (!el || !target) return null;
            const r = el.getBoundingClientRect();
            return {
              id,
              amount: p.to - p.from,
              x: r.left,
              y: r.top,
              dx: target.x - (r.left + r.width / 2),
              dy: target.y - (r.top + r.height / 2),
            };
          })
          .filter(Boolean);
      }
      setExpGain({ ...expGain, stage: nextStage, flyers });
    }, delay);
    return () => clearTimeout(timer);
  }, [expGain]);

  // Sichtbar: alle erreichten Wochen + die nächste (gesperrte), höchstens bis Kursende
  const visibleWeeks = Math.min(weeks, (unlockedWeek || 1) + 1);

  // EXP der sichtbaren Woche merken und ggf. nächste Woche freischalten
  useEffect(() => {
    if (!weekDataReady) return;

    setExpCache((prev) => {
      const old = prev[currentWeek];
      if (old && old.total === exp.total && old.max === exp.max) return prev;
      const next = {
        ...prev,
        [currentWeek]: { total: exp.total, max: exp.max, unlockAt: exp.unlockAt },
      };
      saveWeekExpCache(langId, paceValue, profile, next);
      return next;
    });

    if (
      currentWeek === unlockedWeek &&
      unlockedWeek < weeks &&
      canUnlockNextWeek({ reached: exp.reached, complete: exp.complete }, unlockedAt)
    ) {
      const next = unlockedWeek + 1;
      const at = new Date().toISOString();
      setUnlockedWeek(next);
      setUnlockedAt(at);
      saveUnlockedWeek(langId, paceValue, profile, next);
      saveUnlockedAt(langId, paceValue, profile, next, at);
    }
  }, [
    weekDataReady,
    currentWeek,
    unlockedWeek,
    exp.total,
    exp.max,
    exp.unlockAt,
    exp.reached,
    exp.complete,
    unlockedAt,
    langId,
    paceValue,
    profile,
    weeks,
  ]);

  // Offene Angriffe von Freunden (fehlt das SQL dafür, bleibt die Liste leer)
  const [incomingAttacks, setIncomingAttacks] = useState([]);
  useEffect(() => {
    let cancelled = false;
    loadFriendDuels()
      .then((d) => !cancelled && setIncomingAttacks(d.incoming))
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  // Gesamt-EXP über alle Wochen (für Freunde), einmal pro Öffnen des Hauptmenüs
  const [totalExp, setTotalExp] = useState(null);
  useEffect(() => {
    let cancelled = false;
    setTotalExp(null);
    loadTotalExp(langId, paceValue, profile).then((value) => {
      if (!cancelled) setTotalExp(value);
    });
    return () => {
      cancelled = true;
    };
  }, [langId, paceValue, profile]);

  // Kennzahlen für den Freundes-Tab ablegen (nur Stand der neuesten Woche)
  useEffect(() => {
    if (!weekDataReady || !progressLoaded || currentWeek !== unlockedWeek) return;
    const masteredWords = Object.values(progress || {}).filter(
      (state) => state && state.box >= 4
    ).length;
    publishFriendStats({
      languageId: langId,
      pace: paceValue,
      currentWeek,
      weekExp: exp.total,
      weekExpMax: exp.max,
      masteredWords,
      totalExp,
    });
  }, [
    totalExp,
    weekDataReady,
    progressLoaded,
    currentWeek,
    unlockedWeek,
    progress,
    exp.total,
    exp.max,
    langId,
    paceValue,
  ]);

  // ---------- KARUSSELL ----------
  const scrollToWeek = useCallback((week, smooth = true) => {
    const el = carouselRef.current;
    const card = el && el.children[week - 1];
    if (!card) return;
    el.scrollTo({
      left: card.offsetLeft - (el.clientWidth - card.clientWidth) / 2,
      behavior: smooth ? "smooth" : "auto",
    });
  }, []);

  // Nach dem Laden ohne Animation zur aktuellen Woche springen
  useEffect(() => {
    if (unlockedWeek == null || initialScrollDone.current) return;
    initialScrollDone.current = true;
    scrollToWeek(currentWeek, false);
  }, [unlockedWeek, currentWeek, scrollToWeek]);

  // Die Karte, die beim Wischen überwiegend sichtbar ist, wird zur aktuellen Woche
  useEffect(() => {
    const el = carouselRef.current;
    if (!el || typeof IntersectionObserver === "undefined") return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.6) {
            const week = Number(entry.target.dataset.week);
            setCurrentWeek((w) => (w === week ? w : week));
          }
        });
      },
      { root: el, threshold: 0.6 }
    );

    Array.from(el.children).forEach((card) => observer.observe(card));
    return () => observer.disconnect();
  }, [visibleWeeks]);

  const goToWeek = (week) => {
    scrollToWeek(Math.max(1, Math.min(visibleWeeks, week)));
  };

  const toggleSettings = () => setShowSettings((s) => !s);

  const handleDevReset = async () => {
    const confirmReset = window.confirm(
      "Willst du wirklich deinen kompletten Lernfortschritt und die Konfiguration zurücksetzen, sodass du eingeloggt wieder bei der Sprachauswahl startest?"
    );
    if (!confirmReset) return;

    try {
      const { data: userData, error: userError } =
        await getSessionUser();
      const userId = userData?.user?.id || null;

      if (userError) {
        console.warn("DevReset: getUser error:", userError);
        alert("Fehler beim Abrufen des Users: " + userError.message);
      }

      if (userId) {
        // Vokabel-Fortschritt (alle Sprachen/Profiles)
        const { error: progressError } = await supabase
          .from("user_progress")
          .delete()
          .eq("user_id", userId);

        if (progressError) {
          console.warn("DevReset: user_progress delete error:", progressError);
          alert(
            "Fehler beim Löschen von user_progress: " +
              progressError.message
          );
        }

        // Weekly-Sets (alle Konfigurationen)
        const { error: weeklyError } = await supabase
          .from("weekly_sets")
          .delete()
          .eq("user_id", userId);

        if (weeklyError) {
          console.warn("DevReset: weekly_sets delete error:", weeklyError);
          alert("Fehler beim Löschen von weekly_sets: " + weeklyError.message);
        }

        // Profile-Settings nur für aktuelles Profil
        const { error: settingsError } = await supabase
          .from("profile_settings")
          .delete()
          .eq("user_id", userId)
          .eq("profile_id", profile);

        if (settingsError) {
          console.warn(
            "DevReset: profile_settings delete error:",
            settingsError
          );
          alert(
            "Fehler beim Löschen von profile_settings: " +
              settingsError.message
          );
        }

        const { error: profileProgError } = await supabase
          .from("profile_progress")
          .delete()
          .eq("user_id", userId)
          .eq("profile_id", profile);

        if (profileProgError) {
          console.warn(
            "DevReset: profile_progress delete error:",
            profileProgError
          );
        }

        // Verb-Progress-Tabelle leeren – für aktuelles Profil + Sprache
        const { error: verbProgError } = await supabase
          .from("verb_progress")
          .delete()
          .eq("user_id", userId)
          .eq("profile_id", profile)
          .eq("language_id", langId);

        if (verbProgError) {
          console.warn(
            "DevReset: verb_progress delete error:",
            verbProgError
          );
        }

        // Lückentext-Progress für diese Config löschen (Helper, same keys wie load/save)
        await resetSentenceClozeProgress(
          userId,
          profile,
          langId,
          paceValue
        );

        // Phrasen-Progress für diese Config löschen (Helper)
        await resetPhraseProgress(
          userId,
          profile,
          langId,
          paceValue
        );
      }
    } catch (e) {
      console.warn("DevReset: exception while resetting remote data:", e);
      alert("Unerwarteter Fehler beim Reset: " + (e.message || e));
    }

    // Freie Übersetzung: lokal und remote (eigener Helper, prüft Login selbst)
    await resetTranslationProgress(profile, langId);

    // Themen-Wochen: auf dem Gerät gemerkte bestandene Tests (siehe TopicPractice.jsx)
    try {
      const topicPrefix = `fluent_topic_progress_${profile}_${langId}_`;
      Object.keys(window.localStorage)
        .filter((k) => k.startsWith(topicPrefix))
        .forEach((k) => window.localStorage.removeItem(k));
    } catch (e) {
      console.warn("DevReset: topic progress reset failed:", e);
    }

    // Lokale Speicher zurücksetzen
    resetProgress(langId, profile);
    resetWeeklySet(langId, paceValue, weeklyWords, profile, visibleWeeks);
    resetUnlockedWeek(langId, paceValue, profile);

    // Verb-Fortschritt für alle Wochen zurücksetzen (local + remote helper)
    for (let w = 1; w <= visibleWeeks; w++) {
      await resetVerbProgress(langId, profile, w);
    }

    if (typeof window !== "undefined") {
      try {
        const progressKey = `fluent_progress_${profile || "default"}_${
          langId || "generic"
        }`;
        window.localStorage.removeItem(progressKey);

        for (let w = 1; w <= visibleWeeks; w++) {
          const weeklyKey = getWeeklyKey(
            langId,
            paceValue,
            weeklyWords,
            profile,
            w
          );
          window.localStorage.removeItem(weeklyKey);
        }

        // Satz-History für Lückentexte löschen (falls genutzt)
        const sentenceHistoryKey = `fluent_sentence_history_${
          profile || "default"
        }_${langId || "generic"}`;
        window.localStorage.removeItem(sentenceHistoryKey);
      } catch (e) {
        console.warn("DevReset: localStorage cleanup failed:", e);
      }
    }

    setShowSettings(false);
    setWeeklySet([]);
    setProgress({});
    setVerbStates({});
    setGapsCountWeek(0);   // Lückentext-Progress im State nullen
    setPhraseCountWeek(0); // Phrasen-Progress im State nullen
    setTranslationCountWeek(0); // Übersetzungs-Progress im State nullen
    setCurrentWeek(1);
    setUnlockedWeek(1);
    setExpCache({});
    const resetAt = new Date().toISOString();
    setUnlockedAt(resetAt);
    saveUnlockedAt(langId, paceValue, profile, 1, resetAt);
    if (onHardReset) {
      onHardReset();
    }
  };

  // Freunde-Symbol und Zahnrad für Einstellungen & Konto – sitzen im Kopf jeder Wochenkarte
  const headerActions = (
    <>
      {onOpenFriends && (
        <button
          type="button"
          className="menu-icon-btn"
          onClick={onOpenFriends}
          title="Profil & Freunde"
          aria-label="Profil & Freunde"
        >
          <Icon name="users" />
        </button>
      )}
      <button
        type="button"
        className={`menu-icon-btn${showSettings ? " menu-icon-btn--active" : ""}`}
        onClick={toggleSettings}
        title="Einstellungen & Konto"
        aria-label="Einstellungen & Konto"
        aria-expanded={showSettings}
      >
        {"\u2699"}
      </button>
    </>
  );

  return (
    <div style={{ position: "relative" }}>
      {/* Flyout-Menü */}
      {showSettings && (
        <div className="menu-flyout">
          <Panel variant="muted">
            {userEmail && <InfoRow label="Eingeloggt als" value={userEmail} />}
            <InfoRow label="Sprache" value={labelForLanguage(language)} />
            <InfoRow label="Tempo" value={labelForPace(pace)} />
            <InfoRow label="Zielzeit" value={`${weeks} Wochen`} />
            <InfoRow label="Pensum" value={`${weeklyWords} Wörter/Woche`} />
          </Panel>

          <Stack gap={2} className="menu-flyout__section">
            <Button block size="sm" variant="secondary" onClick={onBack}>
              Lern-Einstellungen ändern
            </Button>
            {onLogout && (
              <Button block size="sm" variant="secondary" onClick={onLogout}>
                Logout
              </Button>
            )}
          </Stack>

          <Stack gap={2} className="menu-flyout__section">
            <Eyebrow>Developer</Eyebrow>
            <Button
              block
              size="sm"
              variant="ghost"
              onClick={() => onOpenDebugVocab && onOpenDebugVocab()}
            >
              Weekly-Vokabeln anzeigen
            </Button>
            <Button
              block
              size="sm"
              variant="ghost"
              onClick={() => onOpenDiagnostics && onOpenDiagnostics()}
            >
              Diagnose-Panel
            </Button>
            <Button block size="sm" variant="danger" onClick={handleDevReset}>
              Wochen-Vokabeln & Konfiguration zurücksetzen
            </Button>
          </Stack>
        </div>
      )}

      {incomingAttacks.map((a) => (
        <AttackBanner key={a.id} attack={a} onDefend={() => onOpenDefense?.(a)} />
      ))}

      <div
        ref={carouselRef}
        className="week-carousel"
      >
        {Array.from({ length: visibleWeeks }, (_, i) => i + 1).map((week) => {
          const locked = unlockedWeek == null || week > unlockedWeek;
          const isCurrent = week === currentWeek;
          const isLatest = week === unlockedWeek;

          // EXP dieser Karte: live für die sichtbare Woche, sonst zuletzt bekannt
          const weekExp =
            isCurrent && weekDataReady
              ? { total: exp.total, max: exp.max, unlockAt: exp.unlockAt }
              : expCache[week] || null;

          if (locked) {
            const prev =
              week - 1 === currentWeek && weekDataReady
                ? exp
                : expCache[week - 1];
            const prevReached = prev && prev.max > 0 && prev.total >= prev.unlockAt;
            const lockHint = !prev
              ? `Erreiche 90 % der EXP in Woche ${week - 1}`
              : prevReached
              ? `Ab nächstem Montag frei oder mit 100 % in Woche ${week - 1}`
              : `Noch ${Math.max(0, prev.unlockAt - prev.total)} EXP in Woche ${week - 1}`;

            // Ausgegraute Vorschau: gleicher Aufbau wie eine offene Woche, alles auf 0
            const previewTranslations = getTranslationWeekTarget(week, paceValue);
            const previewExp = computeWeekExp({
              pace: paceValue,
              vocabTarget: weeklyWords,
              gapsTarget: gapsWeeklyTarget,
              phrasesTarget: phrasesWeeklyTarget,
              translationsTarget: previewTranslations,
            });
            const previewTasks = getWeeklyTasks(
              pace,
              weeklyWords,
              0,
              0,
              0,
              gapsWeeklyTarget,
              0,
              phrasesWeeklyTarget,
              getPhraseWeekPlan(week, paceValue, parseLanguageId(langId)?.source),
              0,
              previewTranslations
            );

            return (
              <div
                key={week}
                data-week={week}
                className="week-card week-card-locked"
              >
                <WeekCardHeader
                  week={week}
                  badge="locked"
                  action={headerActions}
                />
                <div className="week-locked-area">
                  <div className="week-preview" aria-hidden="true">
                    <ExpSummary
                      weekExp={{
                        total: 0,
                        max: previewExp.max,
                        unlockAt: previewExp.unlockAt,
                      }}
                    />
                    <Eyebrow className="week-tasks-label">Aufgaben</Eyebrow>
                    <Stack gap={3} className="week-tasks">
                      {previewTasks
                        .filter((t) => previewExp.parts[t.id])
                        .map((t) => (
                          <div key={t.id} className="ui-option task-card">
                            <div className="task-card__header">
                              <span className="ui-option__label">{t.title}</span>
                              <span className="task-card__count">
                                0 / {previewExp.parts[t.id].max} EXP
                              </span>
                            </div>
                            <div className="ui-option__hint">{t.description}</div>
                            <ProgressBar
                              className="task-card__progress"
                              value={0}
                              tone="exp"
                            />
                          </div>
                        ))}
                    </Stack>
                  </div>
                  {/* Schloss mit Hinweis mittig über der Vorschau */}
                  <div className="week-locked">
                    <Icon name="lock" className="week-locked__icon" />
                    <span>{lockHint}</span>
                  </div>
                </div>
              </div>
            );
          }

          const done = week < unlockedWeek;

          return (
            <div
              key={week}
              data-week={week}
              className={`week-card${isCurrent ? " week-card-current" : ""}`}
            >
              <WeekCardHeader
                week={week}
                badge={done ? "done" : isLatest ? "current" : null}
                action={headerActions}
              />

              <ExpSummary
                weekExp={weekExp}
                gain={isCurrent && expGain ? expGain : null}
                barRef={isCurrent ? barRef : null}
                hint={
                  !weekExp
                    ? null
                    : week === weeks
                    ? weekExp.total < weekExp.unlockAt
                      ? `Noch ${weekExp.unlockAt - weekExp.total} EXP bis zum Kursabschluss`
                      : "Letzte Woche geschafft – Kurs abgeschlossen!"
                    : isLatest && weekExp.total < weekExp.unlockAt
                    ? `Noch ${weekExp.unlockAt - weekExp.total} EXP bis Woche ${
                        week + 1
                      }`
                    : isLatest
                    ? `Schau nächsten Montag wieder vorbei oder löse 100 % von Woche ${week}`
                    : `Woche ${week + 1} ist freigeschaltet`
                }
              />

              {isCurrent && (
                <Eyebrow className="week-tasks-label">Aufgaben</Eyebrow>
              )}

              {isCurrent && (
                <Stack gap={3} className="week-tasks">
                  {tasks.map((task) => {
                    const part = exp.parts[task.id];
                    const gain = expGain && expGain.parts[task.id];
                    const shownExp =
                      gain && expGain.stage === "start" ? gain.from : part && part.exp;

                    return (
                      <button
                        key={task.id}
                        type="button"
                        disabled={!part}
                        className="ui-option task-card"
                        onClick={() => {
                          rememberExp();
                          if (task.id === "vocab") {
                            onOpenFlashcards &&
                              onOpenFlashcards(weeklyWords, currentWeek);
                          } else if (task.id === "verbs") {
                            onOpenVerbs && onOpenVerbs(currentWeek);
                          } else if (task.id === "gaps") {
                            onOpenGaps && onOpenGaps(currentWeek);
                          } else if (task.id === "phrases") {
                            onOpenPhrases && onOpenPhrases(currentWeek);
                          } else if (task.id === "translations") {
                            onOpenTranslations &&
                              onOpenTranslations(currentWeek);
                          }
                        }}
                      >
                        <div className="task-card__header">
                          <span className="ui-option__label">{task.title}</span>
                          {gain && (
                            <span
                              ref={(el) => {
                                gainRefs.current[task.id] = el;
                              }}
                              className={`exp-gain-pop${
                                expGain.stage === "start" || expGain.stage === "tasks"
                                  ? ""
                                  : " exp-gain-pop--gone"
                              }`}
                            >
                              +{gain.to - gain.from} EXP
                            </span>
                          )}
                          {part && part.exp >= part.max ? (
                            <Badge tone="mint">{"\u2713"} {part.exp} / {part.max} EXP</Badge>
                          ) : (
                            <span className="task-card__count">
                              {part ? `${part.exp} / ${part.max} EXP` : "bald verfügbar"}
                            </span>
                          )}
                        </div>

                        <div className="ui-option__hint">{task.description}</div>

                        {part && (
                          <ProgressBar
                            className="task-card__progress"
                            value={part.max > 0 ? shownExp / part.max : 0}
                            gain={gain && part.max > 0 ? gain.to / part.max : null}
                            tone="exp"
                          />
                        )}
                      </button>
                    );
                  })}
                </Stack>
              )}
            </div>
          );
        })}
      </div>

      {/* Navigation: Pfeile + Punkte */}
      <div className="week-nav">
        <button
          type="button"
          className="week-nav-arrow"
          onClick={() => goToWeek(currentWeek - 1)}
          disabled={currentWeek <= 1}
          aria-label="Vorherige Woche"
        >
          {"\u2039"}
        </button>
        {Array.from({ length: visibleWeeks }, (_, i) => i + 1).map((week) => (
          <button
            key={week}
            type="button"
            className={`week-dot${week === currentWeek ? " active" : ""}${
              unlockedWeek != null && week > unlockedWeek ? " locked" : ""
            }`}
            onClick={() => goToWeek(week)}
            aria-label={`Woche ${week}`}
          />
        ))}
        <button
          type="button"
          className="week-nav-arrow"
          onClick={() => goToWeek(currentWeek + 1)}
          disabled={currentWeek >= visibleWeeks}
          aria-label="Nächste Woche"
        >
          {"\u203A"}
        </button>
      </div>

      {expGain &&
        expGain.stage === "fly" &&
        createPortal(
          expGain.flyers.map((f) => <ExpFlyer key={f.id} flyer={f} />),
          document.body
        )}
    </div>
  );
}

/* ---------- Helper-Komponenten ---------- */

const BADGES = {
  done: { text: "\u2713 geschafft", tone: "mint" },
  current: { text: "aktuell", tone: "blue" },
  locked: { text: "gesperrt", tone: "neutral" },
};

function WeekCardHeader({ week, badge, action }) {
  const b = badge ? BADGES[badge] : null;
  return (
    <div className="week-card-header">
      <div className="week-card-header__lead">
        <span className="week-card-header__title">Woche {week}</span>
        {b && <Badge tone={b.tone}>{b.text}</Badge>}
      </div>
      <div className="week-card-header__actions">{action}</div>
    </div>
  );
}

// „+EXP“, das von der Aufgabe in den großen Balken fliegt: wird schneller und
// schrumpft, bis es im Balken verschwindet
function ExpFlyer({ flyer }) {
  const ref = useRef(null);
  const { dx, dy } = flyer;

  useEffect(() => {
    const el = ref.current;
    if (!el || !el.animate) return;
    const animation = el.animate(
      [
        { transform: "translate(0, 0) scale(1)", opacity: 1 },
        { transform: `translate(${dx * 0.8}px, ${dy * 0.8}px) scale(0.45)`, opacity: 1, offset: 0.8 },
        { transform: `translate(${dx}px, ${dy}px) scale(0.1)`, opacity: 0 },
      ],
      {
        duration: EXP_GAIN_STEPS.fly[1],
        easing: "cubic-bezier(0.55, 0, 0.9, 0.4)", // beschleunigt bis zum Aufprall
        fill: "forwards",
      }
    );
    return () => animation.cancel();
  }, [dx, dy]);

  return (
    <span
      ref={ref}
      className="exp-flyer"
      style={{ left: flyer.x, top: flyer.y }}
      aria-hidden="true"
    >
      +{flyer.amount} EXP
    </span>
  );
}

function ExpSummary({ weekExp, hint, gain, barRef }) {
  if (!weekExp) {
    return <Note className="exp-summary">EXP werden geladen …</Note>;
  }

  // Während der Animation steht der große Balken bis zur Landung noch auf dem alten Stand
  const charged = !gain || gain.stage === "total";
  const total = charged ? weekExp.total : gain.total.from;
  const ratio = weekExp.max > 0 ? Math.min(1, total / weekExp.max) : 0;
  const gainRatio =
    gain && weekExp.max > 0 ? Math.min(1, gain.total.to / weekExp.max) : null;
  const markerAt = weekExp.max > 0 ? (weekExp.unlockAt / weekExp.max) * 100 : 80;
  const reached = total >= weekExp.unlockAt;

  // Eigene, leicht hinterlegte Fläche, damit der Wochenfortschritt als Block erkennbar ist
  return (
    <Panel variant="muted" className="exp-summary">
      <Eyebrow>Wochenfortschritt</Eyebrow>
      <div className="exp-summary__total">
        <span
          key={charged ? "new" : "old"}
          className={gain && charged ? "exp-summary__value--pop" : undefined}
        >
          {total}
        </span>{" "}
        <span className="exp-summary__max">/ {weekExp.max} EXP</span>
      </div>
      <div ref={barRef} className={`exp-bar${gain && charged ? " exp-bar--glow" : ""}`}>
        {gainRatio != null && (
          <div className="exp-bar-gain" style={{ width: `${Math.round(gainRatio * 100)}%` }} />
        )}
        <div
          className="exp-bar-fill"
          style={{
            width: `${Math.round(ratio * 100)}%`,
            background: reached ? "var(--fluent-mint)" : "var(--fluent-sun)",
          }}
        />
        <div className="exp-bar-marker" style={{ left: `${markerAt}%` }} />
      </div>
      {hint && <div className="exp-summary__hint">{hint}</div>}
    </Panel>
  );
}

function InfoRow({ label, value }) {
  return (
    <div className="menu-info-row">
      <span className="menu-info-row__label">{label}</span>
      <span className="menu-info-row__value">{value}</span>
    </div>
  );
}

/* ---------- Logik für Tasks & Labels ---------- */

function getWeeklyTasks(
  pace,
  weeklyWords,
  vocabDone = 0,
  verbsPercent = 0,
  gapsDone = 0,
  gapsTargetOverride = null,
  phrasesDone = 0,
  phrasesTargetOverride = null,
  phrasePlan = getPhraseWeekPlan(1, pace),
  translationsDone = 0,
  translationsTarget = 3
) {
  const base = { verbs: 20, gaps: 50, phrases: 5 };
  const factor =
    pace === "hard"
      ? 1
      : pace === "medium"
      ? 0.75
      : pace === "easy"
      ? 0.5
      : 0.75;

  const gapsTarget =
    gapsTargetOverride != null
      ? gapsTargetOverride
      : Math.ceil(base.gaps * factor);

  const phrasesBaseTarget = Math.ceil(base.phrases * factor);
  const phrasesTarget =
    phrasesTargetOverride != null ? phrasesTargetOverride : phrasesBaseTarget;

  return [
    {
      id: "vocab",
      title: "Vokabeln",
      description:
        "Neue Wörter dieser Woche, die dir fluent! als Karteikarten vorlegt.",
      target: weeklyWords,
      unit: "Wörter",
      done: vocabDone,
    },
    {
      id: "verbs",
      title: "Konjugation von Verben",
      description:
        "Trainiere die wichtigsten Verbformen im Präsens",
      target: 100,
      unit: "%",
      done: verbsPercent,
    },
    {
      id: "gaps",
      title: "Lückentexte",
      description:
        "Vervollständige Sätze, indem du die richtigen Wörter an die richtige Stelle ziehst.",
      target: gapsTarget,
      unit: "Lückentexte",
      done: gapsDone,
    },
        {
      id: "phrases",
      // Themen-Woche (Zahlen, Uhrzeit …) oder Redewendungen, siehe phraseSchedule.js
      title: phrasePlan.mode === "topic" ? phrasePlan.title : "Phrasen",
      description: phrasePlan.description,
      target: phrasesTarget,
      unit: phrasePlan.mode === "topic" ? "Einheiten" : "Redewendungen",
      done: phrasesDone,
    },
    {
      id: "translations",
      title: "Freie Übersetzung",
      description:
        "Übersetze ganze Sätze selbst: anfangs meist in deine Muttersprache, mit der Zeit immer mehr in deine Lernsprache.",
      target: translationsTarget,
      unit: "Sätze",
      done: translationsDone,
    },

  ];
}


function labelForPace(v) {
  return PACE_LABELS[v] ?? "Unbekannt";
}
